import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetchBlob, apiRequest } from "@/lib/api";
import type {
  AcquirerLookupResponse,
  AdminUsersResponse,
  AndesEmitAttempt,
  AndesEmitAttemptListResponse,
  AndesEmitInput,
  AndesEmitResponse,
  AndesPullPemInput,
  AndesPullPemResponse,
  AndesRequestsResponse,
  Artifact,
  ArtifactValidation,
  ArtifactVisibility,
  ArtifactWithContent,
  CertificateRequest,
  CreateCertificateRequestInput,
  CreateFileArtifactInput,
  CreateInlineArtifactInput,
  CreateRepresentativeInput,
  CreateSubscriberInput,
  DocumentType,
  GenerateCsrForSubscriberResponse,
  GenerateP12ForSubscriberInput,
  GenerateP12ForSubscriberResponse,
  GeneratePfxForSubscriberResponse,
  HealthResponse,
  ListSubscribersParams,
  MailboxListResponse,
  MailboxMessage,
  MailboxMessageDetail,
  SendFromBoxInput,
  PaginatedSubscribers,
  Representative,
  Subscriber,
  TransitionCertificateRequestInput,
  UpdateRepresentativeInput,
  UpdateSubscriberInput,
  UploadArtifactInput,
  UtilityCsrInput,
  UtilityCsrResponse,
  UtilityP12Input,
  UtilityP12Response,
  UtilityPfxInput,
  UtilityPfxResponse,
} from "@/types";

export const qk = {
  health: ["health"] as const,
  adminUsers: ["admin", "users"] as const,
  subscribersList: (params: ListSubscribersParams) => ["subscribers", "list", params] as const,
  subscriber: (id: string) => ["subscribers", id] as const,
  representatives: (subscriberId: string) => ["subscribers", subscriberId, "representatives"] as const,
  certificateRequests: (subscriberId: string) =>
    ["subscribers", subscriberId, "certificate-requests"] as const,
  artifacts: (subscriberId: string) => ["subscribers", subscriberId, "artifacts"] as const,
  artifact: (subscriberId: string, artifactId: string) =>
    ["subscribers", subscriberId, "artifacts", artifactId] as const,
  artifactValidations: (subscriberId: string) =>
    ["subscribers", subscriberId, "artifact-validations"] as const,
  mailbox: (subscriberId: string) => ["subscribers", subscriberId, "mailbox"] as const,
  mailboxMessage: (subscriberId: string, msgId: string) =>
    ["subscribers", subscriberId, "mailbox", msgId] as const,
  andesAttempts: (subscriberId: string) => ["subscribers", subscriberId, "andes-attempts"] as const,
  andesAttempt: (subscriberId: string, attemptId: string) =>
    ["subscribers", subscriberId, "andes-attempts", attemptId] as const,
};

export function useHealth() {
  return useQuery({
    queryKey: qk.health,
    queryFn: () => apiRequest<HealthResponse>("/health"),
    staleTime: 10_000,
  });
}

// ── Admin: all authenticated users (PC-26) ───────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: qk.adminUsers,
    queryFn: () => apiRequest<AdminUsersResponse>("/api/admin/users"),
  });
}

// ── Subscribers ────────────────────────────────────────────────────────────

export function useSubscribers(params: ListSubscribersParams) {
  return useQuery({
    queryKey: qk.subscribersList(params),
    queryFn: () =>
      apiRequest<PaginatedSubscribers>("/api/subscribers", {
        query: params as Record<string, unknown>,
      }),
  });
}

export function useSubscriber(id: string | undefined) {
  return useQuery({
    queryKey: qk.subscriber(id ?? ""),
    enabled: !!id,
    queryFn: () => apiRequest<Subscriber>(`/api/subscribers/${id}`),
  });
}

export function useCreateSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubscriberInput) =>
      apiRequest<Subscriber>("/api/subscribers", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscribers", "list"] });
    },
  });
}

export function useUpdateSubscriber(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSubscriberInput) =>
      apiRequest<Subscriber>(`/api/subscribers/${id}`, {
        method: "PUT",
        body: input,
      }),
    onSuccess: (data) => {
      qc.setQueryData(qk.subscriber(id), data);
      qc.invalidateQueries({ queryKey: ["subscribers", "list"] });
    },
  });
}

/** Archives (soft) — the api's DELETE maps to archive since PC-38; restore via PUT {status:"active"}. */
export function useArchiveSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/api/subscribers/${id}`, { method: "DELETE" }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: qk.subscriber(id) });
      qc.invalidateQueries({ queryKey: ["subscribers", "list"] });
    },
  });
}

// ── Representatives ────────────────────────────────────────────────────────

export function useRepresentatives(subscriberId: string | undefined) {
  return useQuery({
    queryKey: qk.representatives(subscriberId ?? ""),
    enabled: !!subscriberId,
    queryFn: () => apiRequest<Representative[]>(`/api/subscribers/${subscriberId}/representatives`),
  });
}

export function useCreateRepresentative(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRepresentativeInput) =>
      apiRequest<Representative>(`/api/subscribers/${subscriberId}/representatives`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.representatives(subscriberId) });
    },
  });
}

export function useUpdateRepresentative(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ repId, input }: { repId: string; input: UpdateRepresentativeInput }) =>
      apiRequest<Representative>(`/api/subscribers/${subscriberId}/representatives/${repId}`, {
        method: "PUT",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.representatives(subscriberId) });
    },
  });
}

export function useDeleteRepresentative(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (repId: string) =>
      apiRequest<void>(`/api/subscribers/${subscriberId}/representatives/${repId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.representatives(subscriberId) });
    },
  });
}

// ── Certificate Requests ───────────────────────────────────────────────────

export function useCertificateRequests(subscriberId: string | undefined) {
  return useQuery({
    queryKey: qk.certificateRequests(subscriberId ?? ""),
    enabled: !!subscriberId,
    queryFn: () =>
      apiRequest<CertificateRequest[]>(`/api/subscribers/${subscriberId}/certificate-requests`),
  });
}

export function useCreateCertificateRequest(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCertificateRequestInput) =>
      apiRequest<CertificateRequest>(`/api/subscribers/${subscriberId}/certificate-requests`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.certificateRequests(subscriberId) });
    },
  });
}

export function useTransitionCertificateRequest(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reqId,
      input,
    }: {
      reqId: string;
      input: TransitionCertificateRequestInput;
    }) =>
      apiRequest<CertificateRequest>(
        `/api/subscribers/${subscriberId}/certificate-requests/${reqId}`,
        { method: "PATCH", body: input }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.certificateRequests(subscriberId) });
    },
  });
}

export function useSetCertificateValidity(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reqId,
      validFrom,
      validUntil,
    }: {
      reqId: string;
      validFrom: string | null;
      validUntil: string | null;
    }) =>
      apiRequest<CertificateRequest>(
        `/api/subscribers/${subscriberId}/certificate-requests/${reqId}/validity`,
        { method: "PATCH", body: { validFrom, validUntil } }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.certificateRequests(subscriberId) });
    },
  });
}

// ── Artifacts ──────────────────────────────────────────────────────────────

export function useArtifacts(subscriberId: string | undefined) {
  return useQuery({
    queryKey: qk.artifacts(subscriberId ?? ""),
    enabled: !!subscriberId,
    queryFn: () => apiRequest<Artifact[]>(`/api/subscribers/${subscriberId}/artifacts`),
  });
}

export function useArtifactValidations(subscriberId: string | undefined) {
  return useQuery({
    queryKey: qk.artifactValidations(subscriberId ?? ""),
    enabled: !!subscriberId,
    queryFn: () =>
      apiRequest<ArtifactValidation[]>(`/api/subscribers/${subscriberId}/artifact-validations`),
  });
}

export function useArtifactContent(subscriberId: string, artifactId: string | null) {
  return useQuery({
    queryKey: qk.artifact(subscriberId, artifactId ?? ""),
    enabled: !!artifactId,
    queryFn: () =>
      apiRequest<ArtifactWithContent>(`/api/subscribers/${subscriberId}/artifacts/${artifactId}`),
  });
}

export function useCreateFileArtifact(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFileArtifactInput) =>
      apiRequest<Artifact>(`/api/subscribers/${subscriberId}/artifacts`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

export function useUploadArtifact(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadArtifactInput) => {
      const form = new FormData();
      form.append("file", input.file);
      form.append("type", input.type);
      if (input.fileName) form.append("fileName", input.fileName);
      if (input.visibility) form.append("visibility", input.visibility);
      if (input.representativeId) form.append("representativeId", input.representativeId);
      if (input.certificateRequestId)
        form.append("certificateRequestId", input.certificateRequestId);
      return apiRequest<Artifact>(`/api/subscribers/${subscriberId}/artifacts/upload`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

export function useDownloadArtifactFile(subscriberId: string) {
  return useMutation({
    mutationFn: (artifactId: string) =>
      apiFetchBlob(`/api/subscribers/${subscriberId}/artifacts/${artifactId}/download`),
  });
}

export function useCreateInlineArtifact(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInlineArtifactInput) =>
      apiRequest<Artifact>(`/api/subscribers/${subscriberId}/artifacts/inline`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

export function useSetArtifactVisibility(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      artifactId,
      visibility,
    }: {
      artifactId: string;
      visibility: ArtifactVisibility;
    }) =>
      apiRequest<Artifact>(`/api/subscribers/${subscriberId}/artifacts/${artifactId}`, {
        method: "PATCH",
        body: { visibility },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: qk.artifact(subscriberId, data.id) });
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

export function useDeleteArtifact(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (artifactId: string) =>
      apiRequest<void>(`/api/subscribers/${subscriberId}/artifacts/${artifactId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

// ── Generate (subscriber-linked) ───────────────────────────────────────────

export function useGenerateCsrForSubscriber(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<GenerateCsrForSubscriberResponse>(
        `/api/subscribers/${subscriberId}/generate-csr`,
        { method: "POST" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

export function useGeneratePfxForSubscriber(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pem: string }) =>
      apiRequest<GeneratePfxForSubscriberResponse>(
        `/api/subscribers/${subscriberId}/generate-pfx`,
        { method: "POST", body: input }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

export function useGenerateP12ForSubscriber(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateP12ForSubscriberInput) =>
      apiRequest<GenerateP12ForSubscriberResponse>(
        `/api/subscribers/${subscriberId}/generate-p12`,
        { method: "POST", body: input }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
    },
  });
}

// ── Orchestrator proxy ─────────────────────────────────────────────────────

export function useLookupAcquirer() {
  return useMutation({
    mutationFn: (input: { documentType: DocumentType; documentNumber: string }) =>
      apiRequest<AcquirerLookupResponse>("/api/orchestrator/acquirer", {
        query: { documentType: input.documentType, documentNumber: input.documentNumber },
      }),
  });
}

// ── Utilities (stateless) ──────────────────────────────────────────────────

export function useUtilityGenerateCsr() {
  return useMutation({
    mutationFn: (input: UtilityCsrInput) =>
      apiRequest<UtilityCsrResponse>("/api/utilities/csr/generate", {
        method: "POST",
        body: input,
      }),
  });
}

export function useUtilityGeneratePfx() {
  return useMutation({
    mutationFn: (input: UtilityPfxInput) =>
      apiRequest<UtilityPfxResponse>("/api/utilities/pfx/generate", {
        method: "POST",
        body: input,
      }),
  });
}

export function useUtilityGenerateP12() {
  return useMutation({
    mutationFn: (input: UtilityP12Input) =>
      apiRequest<UtilityP12Response>("/api/utilities/p12/generate", {
        method: "POST",
        body: input,
      }),
  });
}

// ── Andes request queue (GET /api/andes/requests) ───────────────────────
export function useAndesRequests(
  filters: { dateFrom?: string; dateTo?: string },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["andes", "requests", filters] as const,
    enabled: options?.enabled ?? true,
    queryFn: () =>
      apiRequest<AndesRequestsResponse>("/api/andes/requests", {
        query: {
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      }),
  });
}

// ── Andes emission via certmanager BFF (POST /api/subscribers/{id}/emit-andes) ─
export function useAndesEmitCertificate(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AndesEmitInput) =>
      apiRequest<AndesEmitResponse>(`/api/subscribers/${subscriberId}/emit-andes`, {
        method: "POST",
        body: input,
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.andesAttempts(subscriberId) });
    },
  });
}

export function useAndesPullPem(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AndesPullPemInput) =>
      apiRequest<AndesPullPemResponse>(`/api/subscribers/${subscriberId}/pull-andes-pem`, {
        method: "POST",
        body: input,
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.artifacts(subscriberId) });
      qc.invalidateQueries({ queryKey: qk.certificateRequests(subscriberId) });
    },
  });
}

// ── Mailbox (per-subscriber shared box; PC-19) ──────────────────────────────
export function useMailbox(subscriberId: string | undefined) {
  return useQuery({
    queryKey: qk.mailbox(subscriberId ?? ""),
    enabled: !!subscriberId,
    queryFn: () => apiRequest<MailboxListResponse>(`/api/subscribers/${subscriberId}/mailbox`),
  });
}

export function useMailboxMessage(subscriberId: string, msgId: string | null) {
  return useQuery({
    queryKey: qk.mailboxMessage(subscriberId, msgId ?? ""),
    enabled: !!msgId,
    queryFn: () =>
      apiRequest<MailboxMessageDetail>(`/api/subscribers/${subscriberId}/mailbox/${msgId}`),
  });
}

export function useSendFromBox(subscriberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendFromBoxInput) =>
      apiRequest<MailboxMessage>(`/api/subscribers/${subscriberId}/mailbox/send`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mailbox(subscriberId) });
    },
  });
}

export function useDownloadMailboxAttachment(subscriberId: string) {
  return useMutation({
    mutationFn: ({ msgId, index }: { msgId: string; index: number }) =>
      apiFetchBlob(
        `/api/subscribers/${subscriberId}/mailbox/${msgId}/attachments/${index}/download`
      ),
  });
}

export function useAndesAttempts(subscriberId: string | undefined) {
  return useQuery({
    queryKey: qk.andesAttempts(subscriberId ?? ""),
    enabled: !!subscriberId,
    queryFn: () =>
      apiRequest<AndesEmitAttemptListResponse>(`/api/subscribers/${subscriberId}/andes-attempts`),
  });
}

export function useAndesAttempt(subscriberId: string, attemptId: string | null) {
  return useQuery({
    queryKey: qk.andesAttempt(subscriberId, attemptId ?? ""),
    enabled: !!attemptId,
    queryFn: () =>
      apiRequest<AndesEmitAttempt>(`/api/subscribers/${subscriberId}/andes-attempts/${attemptId}`),
  });
}

export function useAndesAttemptShot(subscriberId: string, attemptId: string, name: string) {
  return useQuery({
    queryKey: [...qk.andesAttempt(subscriberId, attemptId), "shot", name] as const,
    queryFn: async () => {
      const { blob } = await apiFetchBlob(
        `/api/subscribers/${subscriberId}/andes-attempts/${attemptId}/shots/${encodeURIComponent(name)}`
      );
      return URL.createObjectURL(blob);
    },
  });
}

export function useDownloadAndesAttemptPackage(subscriberId: string) {
  return useMutation({
    mutationFn: async (attemptId: string) => {
      const { blob, fileName } = await apiFetchBlob(
        `/api/subscribers/${subscriberId}/andes-attempts/${attemptId}/package`
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "soporte.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}
